import type { FC } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Box, Checkbox, Typography, useTheme } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

interface DraggableColumnProps {
    index: number;
    columnName: string;
    visibility: boolean;
    moveColumn: (dragIndex: number, hoverIndex: number) => void;
    header: string;
    toggleVisibility: (columnName: string, visible: boolean) => void;
}

export const DraggableColumn: FC<DraggableColumnProps> = ({
    index,
    columnName,
    visibility,
    header,
    toggleVisibility,
}) => {
    const theme = useTheme();

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: columnName,
        data: {
            type: "column",
            index,
        },
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <Box
            ref={setNodeRef}
            style={style}
            sx={{
                display: "flex",
                alignItems: "center",
                gap: theme.spacing(2),
                padding: theme.spacing(1.5),
                border: "1px solid #E7E6E4",
                borderRadius: "8px",
                backgroundColor: "white",
                cursor: isDragging ? "grabbing" : "grab",
                "&:hover": {
                    borderColor: "#D0D0D0",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                },
            }}
        >
            {/* Drag Handle */}
            <Box
                {...attributes}
                {...listeners}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    color: "#999",
                    cursor: "grab",
                    "&:active": {
                        cursor: "grabbing",
                    },
                }}
            >
                <DragIndicatorIcon fontSize="small" />
            </Box>

            {/* Checkbox */}
            <Checkbox
                checked={visibility}
                onChange={(event) => {
                    event.stopPropagation();
                    toggleVisibility(columnName, event.target.checked);
                }}
                size="small"
                sx={{
                    color: theme.palette.primary.main,
                    "&.Mui-checked": {
                        color: theme.palette.primary.main,
                    },
                    "&:hover": {
                        backgroundColor: `${theme.palette.primary.main}0A`,
                    },
                }}
            />

            {/* Column Header */}
            <Typography
                variant="small-medium"
                sx={{
                    flex: 1,
                    color: "#393A36",
                }}
            >
                {header}
            </Typography>
        </Box>
    );
};
